import {
  Box,
  Stack,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Avatar,
  SelectChangeEvent,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { alpha, useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import LicenseStatusCard from "../components/LicenseStatusCard";
import LicenseDistributionChart from "../components/LicenseDistributionChart";
import ExpirationChart from "../components/ExpirationChart";
import AlertsPanel from "../components/AlertsPanel";
import {
  DashboardMetrics,
  BusinessMetrics,
  MasterDashboard,
  DealerRanking,
} from "../types";
import TenantDashboard from "../components/TenantDashboard";
import { useAuth } from "../state/AuthContext";
import { useToast } from "../state/ToastContext";
import { useTranslation } from "react-i18next";
import { parseErrorMessage } from "../utils/errorUtils";

// Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

// Standard stat card - clean and simple
function StatCard({
  title,
  value,
  subtitle,
  loading,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  loading?: boolean;
}) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton width={80} height={40} />
        ) : (
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// KPI card with trend indicator
function KPICard({
  title,
  value,
  trend,
  trendLabel,
  loading,
}: {
  title: string;
  value: string | number;
  trend?: "up" | "down" | "flat";
  trendLabel?: string;
  loading?: boolean;
}) {
  const theme = useTheme();

  const getTrendColor = () => {
    if (trend === "up") return theme.palette.success.main;
    if (trend === "down") return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const TrendIcon =
    trend === "up"
      ? TrendingUpIcon
      : trend === "down"
        ? TrendingDownIcon
        : TrendingFlatIcon;

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton width={80} height={40} />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" fontWeight={600}>
              {value}
            </Typography>
            {trend && trendLabel && (
              <Chip
                size="small"
                icon={<TrendIcon sx={{ fontSize: 16 }} />}
                label={trendLabel}
                sx={{
                  height: 24,
                  bgcolor: alpha(getTrendColor(), 0.1),
                  color: getTrendColor(),
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// Period options
const PERIOD_OPTIONS = [
  { value: "7", label: "7 dias" },
  { value: "30", label: "30 dias" },
  { value: "90", label: "90 dias" },
  { value: "180", label: "6 meses" },
  { value: "365", label: "1 ano" },
];

export default function MetricsPage() {
  const { user } = useAuth();
  const { showError } = useToast();
  const theme = useTheme();
  const { t } = useTranslation();

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [businessMetrics, setBusinessMetrics] =
    useState<BusinessMetrics | null>(null);
  const [masterDashboard, setMasterDashboard] =
    useState<MasterDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");

  const isMaster = user?.role === "master_full";
  const canViewBusinessMetrics =
    user?.role === "master_full" || user?.role === "dealer_admin";

  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      try {
        const promises: Promise<any>[] = [
          api.getDashboardMetrics(null),
          canViewBusinessMetrics
            ? api.getBusinessMetrics(null)
            : Promise.resolve(null),
        ];
        if (isMaster) {
          promises.push(api.getMasterDashboard(null, { includeDetails: true }));
        }

        const results = await Promise.all(promises);
        setMetrics(results[0]);
        setBusinessMetrics(results[1]);
        if (isMaster && results[2]) {
          setMasterDashboard(results[2]);
        }
      } catch (err) {
        showError(parseErrorMessage(err, t("metrics.errors.load")));
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
    const interval = setInterval(loadMetrics, 60_000);
    return () => clearInterval(interval);
  }, [canViewBusinessMetrics, isMaster, showError, t]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriod(event.target.value);
  };

  const maxTenantCount = masterDashboard?.dealerRanking?.[0]?.tenantCount || 1;

  return (
    <Stack spacing={3}>
      {/* Header with period selector */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {isMaster
              ? t("metrics.masterTitle", "Painel Master")
              : t("metrics.title", "Dashboard")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {metrics?.generatedAt
              ? t("metrics.updatedAt", {
                  timestamp: new Date(metrics.generatedAt).toLocaleString(
                    t("meta.languageCode", undefined),
                  ),
                })
              : ""}
          </Typography>
        </Box>

        {isMaster && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t("metrics.period", "Período")}</InputLabel>
            <Select
              value={period}
              label={t("metrics.period", "Período")}
              onChange={handlePeriodChange}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {/* Master Dashboard */}
      {isMaster && masterDashboard && (
        <>
          {/* Row 1: Main counts */}
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.master.totalDealers", "Dealers")}
                value={masterDashboard.summary.totalDealers}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.master.totalTenants", "Clientes")}
                value={masterDashboard.summary.totalTenants}
                subtitle={`${masterDashboard.summary.activeTenants} ${t("metrics.master.active", "ativos")}`}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.master.totalUsers", "Usuários")}
                value={masterDashboard.summary.totalUsers}
                subtitle={`${masterDashboard.summary.activeUsers} ${t("metrics.master.active", "ativos")}`}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.master.apps", "Aplicações")}
                value={masterDashboard.summary.totalApplications}
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* Row 2: KPIs with trends */}
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={2}>
              <KPICard
                title={t("metrics.master.churnRate", "Taxa de Churn")}
                value={`${masterDashboard.kpis.churnRate}%`}
                trend={
                  masterDashboard.kpis.churnRate > 10
                    ? "down"
                    : masterDashboard.kpis.churnRate > 5
                      ? "flat"
                      : "up"
                }
                trendLabel={
                  masterDashboard.kpis.churnRate > 10
                    ? "Alto"
                    : masterDashboard.kpis.churnRate > 5
                      ? "Médio"
                      : "Baixo"
                }
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={2}>
              <KPICard
                title={t("metrics.master.demoConversion", "Conversão Demo")}
                value={`${masterDashboard.kpis.demoConversionRate}%`}
                trend={
                  masterDashboard.kpis.demoConversionRate > 30
                    ? "up"
                    : masterDashboard.kpis.demoConversionRate > 15
                      ? "flat"
                      : "down"
                }
                trendLabel={
                  masterDashboard.kpis.demoConversionRate > 30
                    ? "Ótimo"
                    : masterDashboard.kpis.demoConversionRate > 15
                      ? "Ok"
                      : "Baixo"
                }
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={2}>
              <StatCard
                title={t("metrics.master.avgTenants", "Média/Dealer")}
                value={masterDashboard.kpis.avgTenantsPerDealer}
                subtitle={t(
                  "metrics.master.tenantsPerDealer",
                  "clientes/dealer",
                )}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={2}>
              <StatCard
                title={t("metrics.master.demoTenants", "Em Demo")}
                value={masterDashboard.summary.demoTenants}
                subtitle={t("metrics.master.demoActive", "em período de teste")}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={2}>
              <StatCard
                title={t("metrics.master.activeSessions", "Sessões Ativas")}
                value={metrics?.activeSessions ?? 0}
                subtitle={t("metrics.master.allTenants", "em todos os tenants")}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={2}>
              <StatCard
                title={t("metrics.master.externalSessions", "Sessões B2B")}
                value={metrics?.externalSessions ?? 0}
                subtitle={t(
                  "metrics.master.externalApps",
                  "aplicações externas",
                )}
                loading={loading}
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Row 3: Dealer Ranking Table */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("metrics.master.dealerRanking", "Ranking de Dealers")}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell width={50}>#</TableCell>
                    <TableCell>
                      {t("metrics.ranking.dealer", "Dealer")}
                    </TableCell>
                    <TableCell align="right">
                      {t("metrics.ranking.clients", "Clientes")}
                    </TableCell>
                    <TableCell align="right">
                      {t("metrics.ranking.share", "Share")}
                    </TableCell>
                    <TableCell align="right">
                      {t("metrics.ranking.newMonth", "Novos (mês)")}
                    </TableCell>
                    <TableCell align="right">
                      {t("metrics.ranking.growth", "Crescimento")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : masterDashboard.dealerRanking.length > 0 ? (
                    masterDashboard.dealerRanking.map((dealer) => {
                      const isPositive = dealer.growthPercent > 0;
                      const isNegative = dealer.growthPercent < 0;

                      return (
                        <TableRow key={dealer.id} hover>
                          <TableCell>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 12,
                                bgcolor:
                                  dealer.rank <= 3
                                    ? "primary.main"
                                    : "grey.400",
                              }}
                            >
                              {dealer.rank}
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                {dealer.name}
                              </Typography>
                              <Chip
                                label={dealer.code}
                                size="small"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack spacing={0.5}>
                              <Typography variant="body2" fontWeight={600}>
                                {dealer.tenantCount}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={
                                  (dealer.tenantCount / maxTenantCount) * 100
                                }
                                sx={{ height: 4, borderRadius: 2 }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {dealer.marketShare}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {dealer.newThisMonth}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="flex-end"
                              spacing={0.5}
                            >
                              {isPositive && (
                                <TrendingUpIcon
                                  sx={{ fontSize: 16, color: "success.main" }}
                                />
                              )}
                              {isNegative && (
                                <TrendingDownIcon
                                  sx={{ fontSize: 16, color: "error.main" }}
                                />
                              )}
                              {!isPositive && !isNegative && (
                                <TrendingFlatIcon
                                  sx={{ fontSize: 16, color: "text.secondary" }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                color={
                                  isPositive
                                    ? "success.main"
                                    : isNegative
                                      ? "error.main"
                                      : "text.secondary"
                                }
                              >
                                {isPositive ? "+" : ""}
                                {dealer.growthPercent}%
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t("metrics.master.noData", "Nenhum dado disponível")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}

      {/* Standard Dashboard for Dealers (Scoped Stats) */}
      {!isMaster && canViewBusinessMetrics && (
        <Stack spacing={3}>
          {/* Dealer Info Card with Tenant Limit */}
          {metrics?.dealerInfo && (
            <Card
              variant="outlined"
              sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Box>
                    <Typography variant="overline" sx={{ opacity: 0.8 }}>
                      {t("metrics.dealer.yourDealer", "Sua Revenda")}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {metrics.dealerInfo.name}
                    </Typography>
                  </Box>
                  {metrics.dealerInfo.tenantLimit && (
                    <Box
                      sx={{
                        textAlign: { xs: "left", sm: "right" },
                        minWidth: 200,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, mb: 0.5 }}
                      >
                        {t("metrics.dealer.tenantUsage", "Uso de Licenças")}
                      </Typography>
                      <Typography variant="h4" fontWeight={700}>
                        {metrics.dealerInfo.tenantsUsed} /{" "}
                        {metrics.dealerInfo.tenantLimit}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={
                          (metrics.dealerInfo.tenantsUsed /
                            metrics.dealerInfo.tenantLimit) *
                          100
                        }
                        sx={{
                          mt: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.common.white, 0.2),
                          "& .MuiLinearProgress-bar": {
                            bgcolor: theme.palette.common.white,
                          },
                        }}
                      />
                      {metrics.dealerInfo.tenantsUsed >=
                        metrics.dealerInfo.tenantLimit && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "warning.light",
                            mt: 0.5,
                            display: "block",
                          }}
                        >
                          {t(
                            "metrics.dealer.limitReached",
                            "Limite atingido! Contate o suporte para aumentar.",
                          )}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Stat Cards */}
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.cards.tenants", "Clientes")}
                value={metrics?.tenants ?? 0}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.cards.users", "Usuários")}
                value={metrics?.users ?? 0}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.cards.applications", "Aplicações")}
                value={metrics?.applications ?? 0}
                loading={loading}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title={t("metrics.cards.available", "Disponíveis")}
                value={
                  metrics?.dealerInfo?.tenantLimit
                    ? metrics.dealerInfo.tenantLimit -
                      metrics.dealerInfo.tenantsUsed
                    : "∞"
                }
                subtitle={t("metrics.cards.newTenants", "novas licenças")}
                loading={loading}
              />
            </Grid>
          </Grid>
        </Stack>
      )}

      {/* Dealer: Application Stats */}
      {!isMaster && canViewBusinessMetrics && metrics?.applicationStats && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t("metrics.apps.distribution", "Distribuição de Aplicativos")}
          </Typography>
          <Grid container spacing={2}>
            {metrics.applicationStats.map((stat, idx) => (
              <Grid key={idx} xs={6} sm={4} md={3} lg={2}>
                <Card variant="outlined">
                  <CardContent
                    sx={{
                      textAlign: "center",
                      p: 2,
                      "&:last-child": { pb: 2 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color="primary.main"
                    >
                      {stat.count}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      title={stat.name}
                    >
                      {stat.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tenant Dashboard */}
      {metrics?.tenantDashboard && !isMaster && (
        <TenantDashboard data={metrics.tenantDashboard} />
      )}

      {/* Fallback Welcome Card */}
      {!isMaster && !canViewBusinessMetrics && !metrics?.tenantDashboard && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              {t("dashboard.welcome.title", "Welcome, {{name}}!", {
                name: user?.displayName,
              })}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto", mb: 2 }}
            >
              {t(
                "dashboard.welcome.subtitle",
                "Use the side menu to access your applications and settings.",
              )}
            </Typography>
            {user?.tenantId && (
              <Typography variant="caption" color="text.disabled">
                Tenant ID: {user.tenantId}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Business Metrics Section */}
      {/* Business Metrics Section */}
      {canViewBusinessMetrics && businessMetrics && (
        <Stack spacing={4}>
          <Divider />

          {/* License Status Section */}
          <Box component="section">
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ mb: 2 }}
            >
              {t("metrics.licenseStatus.title", "Status das Licenças")}
            </Typography>
            <Grid container spacing={3}>
              <Grid xs={12} sm={6} md={3}>
                <LicenseStatusCard
                  title={t("metrics.licenseStatus.active", "Ativas")}
                  value={businessMetrics.summary.activeLicenses}
                  severity="success"
                  loading={loading}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <LicenseStatusCard
                  title={t("metrics.licenseStatus.expiring", "Expirando")}
                  value={businessMetrics.summary.expiringLicenses}
                  severity={
                    businessMetrics.summary.expiringLicenses
                      ? "warning"
                      : "success"
                  }
                  loading={loading}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <LicenseStatusCard
                  title={t("metrics.licenseStatus.expired", "Expiradas")}
                  value={businessMetrics.summary.expiredLicenses}
                  severity={
                    businessMetrics.summary.expiredLicenses
                      ? "error"
                      : "success"
                  }
                  loading={loading}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <LicenseStatusCard
                  title={t("metrics.licenseStatus.demo", "Demo")}
                  value={businessMetrics.summary.demoTenants}
                  severity="info"
                  loading={loading}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Charts Section */}
          <Box component="section">
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ mb: 2 }}
            >
              {t("dashboard.charts.title", "Análises")}
            </Typography>
            <Grid container spacing={3}>
              <Grid xs={12} md={4}>
                <LicenseDistributionChart
                  summary={businessMetrics.summary}
                  loading={loading}
                />
              </Grid>
              <Grid xs={12} md={8}>
                <ExpirationChart
                  data={businessMetrics.expirationTimeline}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Alerts Section */}
          <Box component="section">
            <AlertsPanel alerts={businessMetrics.alerts} loading={loading} />
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
